// Utilidades para manejar fechas de Firestore

/**
 * Convierte un timestamp de Firestore o fecha a un objeto Date de JavaScript
 * @param {any} firestoreTimestamp - Timestamp de Firestore, Date, o string de fecha
 * @returns {Date} Objeto Date de JavaScript
 */
export const parseFirestoreDate = (firestoreTimestamp) => {
  if (!firestoreTimestamp) {
    return null;
  }

  // Si es un timestamp de Firestore (tiene método toDate)
  if (firestoreTimestamp && typeof firestoreTimestamp.toDate === 'function') {
    return firestoreTimestamp.toDate();
  }

  // Si ya es un objeto Date
  if (firestoreTimestamp instanceof Date) {
    return firestoreTimestamp;
  }

  // Si es un string de fecha
  if (typeof firestoreTimestamp === 'string') {
    const parsed = new Date(firestoreTimestamp);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  // Si es un timestamp en milisegundos
  if (typeof firestoreTimestamp === 'number') {
    const parsed = new Date(firestoreTimestamp);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

/**
 * Formatea una fecha para mostrar en la interfaz
 * @param {any} firestoreTimestamp - Timestamp de Firestore, Date, o string de fecha
 * @param {string} locale - Locale para el formato (default: 'es-ES')
 * @returns {string} Fecha formateada o 'Fecha no válida' si hay error
 */
export const formatEventDate = (firestoreTimestamp, locale = 'es-ES') => {
  const date = parseFirestoreDate(firestoreTimestamp);
  
  if (!date) {
    return 'Fecha no válida';
  }

  try {
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Fecha no válida';
  }
};

/**
 * Formatea una fecha con hora para mostrar en la interfaz
 * @param {any} firestoreTimestamp - Timestamp de Firestore, Date, o string de fecha
 * @param {string} locale - Locale para el formato (default: 'es-ES')
 * @returns {string} Fecha y hora formateada o 'Fecha no válida' si hay error
 */
export const formatEventDateTime = (firestoreTimestamp, locale = 'es-ES') => {
  const date = parseFirestoreDate(firestoreTimestamp);
  
  if (!date) {
    return 'Fecha no válida';
  }

  try {
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date time:', error);
    return 'Fecha no válida';
  }
};

/**
 * Verifica si una fecha es en el futuro
 * @param {any} firestoreTimestamp - Timestamp de Firestore, Date, o string de fecha
 * @returns {boolean} true si la fecha es en el futuro
 */
export const isFutureDate = (firestoreTimestamp) => {
  const date = parseFirestoreDate(firestoreTimestamp);
  if (!date) return false;
  
  return date > new Date();
};

/**
 * Verifica si una fecha es hoy
 * @param {any} firestoreTimestamp - Timestamp de Firestore, Date, o string de fecha
 * @returns {boolean} true si la fecha es hoy
 */
export const isToday = (firestoreTimestamp) => {
  const date = parseFirestoreDate(firestoreTimestamp);
  if (!date) return false;
  
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

/**
 * Obtiene la diferencia en días entre una fecha y hoy
 * @param {any} firestoreTimestamp - Timestamp de Firestore, Date, o string de fecha
 * @returns {number} Diferencia en días (positivo si es futuro, negativo si es pasado)
 */
export const getDaysDifference = (firestoreTimestamp) => {
  const date = parseFirestoreDate(firestoreTimestamp);
  if (!date) return 0;
  
  const today = new Date();
  const diffTime = date.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};
